// src/components/Tablo.jsx
import React, { useEffect, useState } from "react";
import SwitchButton from "../components/SwitchButton";
import EditableText from "../components/EditableText";
import { toast } from "react-toastify";
import { apiUrl } from "../config/api";
import { adminAuthHeaders } from "../utils/authHeaders";
import { formatDisplayNumber, parseFormattedNumber } from "../utils/numberFormat";

function Tablo() {
  const PRICE_SELL_BUY_GAP = 1000000;
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [itemStates, setItemStates] = useState({});
  const [categoryStates, setCategoryStates] = useState({});
  const [guestPriceAccess, setGuestPriceAccess] = useState(true);
  
  // ================= دریافت داده‌ها =================
  const fetchCategories = async () => {
    try {
      const res = await fetch(apiUrl("/api/categories"));
      const data = await res.json();
      setCategories(data);
      
      const initialStates = {};
      const initialCategoryStates = {};
      data.forEach(cat => {
        initialCategoryStates[cat.id] = cat.tradeEnabled !== false;
        cat.items.forEach(item => {
          initialStates[item.id] = {
            switch1: item.trade ?? false,
            switch2: item.buy ?? false,
            switch3: item.sell ?? false,
          };
        });
      });
      setItemStates(initialStates);
      setCategoryStates(initialCategoryStates);
      
      if (data.length && !selectedCategory) {
        setSelectedCategory(data[0].id);
      }
    } catch (e) {
      toast.error("خطا در دریافت سربرگ‌ها");
    }
  };

  const fetchGuestPriceAccess = async () => {
    try {
      const res = await fetch(apiUrl("/api/settings/public-prices"));
      const data = await res.json();
      setGuestPriceAccess(data.guestPriceAccess !== false);
    } catch (e) {
      setGuestPriceAccess(true);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchGuestPriceAccess();
  }, []);

  // ================= ارسال Real-time به بک =================
  const syncToServer = async (itemId, switches) => {
    try {
      const res = await fetch(
        apiUrl("/api/category/item/switch"),
        {
          method: "POST",
          headers: adminAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            itemId,
            switches: {
              trade: switches.switch1 || false,
              buy: switches.switch2 || false,
              sell: switches.switch3 || false,
            },
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || "خطا در ذخیره تغییرات وضعیت");
      }
    } catch (e) {
      toast.error("خطای ارتباط با سرور");
    }
  };

  // ================= آپدیت فی خرید/فروش =================
  const normalizePriceNumber = (value) => {
    const normalized = parseFormattedNumber(value, { allowDecimal: false });

    return Math.max(0, Number(normalized) || 0);
  };

  const getCoupledPriceValues = (field, value) => {
    if (field === "valueBuy") {
      const valueBuy = normalizePriceNumber(value);
      return {
        valueBuy: String(valueBuy),
        valueSell: String(valueBuy + PRICE_SELL_BUY_GAP),
      };
    }

    const valueSell = normalizePriceNumber(value);
    return {
      valueBuy: String(Math.max(0, valueSell - PRICE_SELL_BUY_GAP)),
      valueSell: String(valueSell),
    };
  };

  const shouldAutoSyncBuySell = (itemId) =>
    categories.some(
      (category) =>
        category.name?.includes("آبشده") &&
        category.items?.some((item) => item.id === itemId)
    );

  const updateItemValue = async (itemId, values) => {
    try {
      const res = await fetch(
        apiUrl(`/api/item/${itemId}/value`),
        {
          method: "PUT",
          headers: adminAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify(values),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || "خطا در ذخیره مقدار");
      }
    } catch (e) {
      toast.error("خطای ارتباط با سرور");
    }
  };

  const updateCoupledItemValue = async (itemId, field, value) => {
    const values = shouldAutoSyncBuySell(itemId)
      ? getCoupledPriceValues(field, value)
      : { [field]: String(normalizePriceNumber(value)) };

    setCategories((prev) =>
      prev.map((category) => ({
        ...category,
        items: category.items.map((item) =>
          item.id === itemId ? { ...item, ...values } : item
        ),
      }))
    );

    await updateItemValue(itemId, values);
  };

  const adjustItemValue = async (itemId, field, currentValue, amount) => {
    const baseValue = normalizePriceNumber(currentValue);
    const nextValue = Math.max(0, baseValue + amount);
    await updateCoupledItemValue(itemId, field, nextValue);
  };

  // ================= تغییر سوییچ =================
  const handleSwitchChange = (itemId, name, value) => {
    setItemStates((prev) => {
      const updatedItem = { ...(prev[itemId] || {}), [name]: value };
      syncToServer(itemId, updatedItem);
      return { ...prev, [itemId]: updatedItem };
    });
  };

  const syncCategoryToServer = async (categoryId, value) => {
    try {
      const res = await fetch(apiUrl(`/api/category/${categoryId}/switch`), {
        method: "POST",
        headers: adminAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ tradeEnabled: value }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || "خطا در ذخیره وضعیت سربرگ");
      }
    } catch (e) {
      toast.error("خطای ارتباط با سرور");
    }
  };

  const handleCategorySwitchChange = (categoryId, value) => {
    setCategoryStates((prev) => ({ ...prev, [categoryId]: value }));
    setCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? { ...category, tradeEnabled: value }
          : category
      )
    );
    syncCategoryToServer(categoryId, value);
  };

  const handleGuestPriceAccessChange = async (value) => {
    setGuestPriceAccess(value);

    try {
      const res = await fetch(apiUrl("/api/settings/public-prices"), {
        method: "POST",
        headers: adminAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ guestPriceAccess: value }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || "خطا در ذخیره وضعیت کاربر مهمان");
      }
    } catch (e) {
      toast.error("خطای ارتباط با سرور");
    }
  };

  const activeCategory = categories.find((c) => c.id === selectedCategory);

  return (
    <div>
      <div className="SelectedHeader">
        شما در این قسمت می توانید مظنه تابلو خود را به صورت دستی تغییر دهید
      </div>
      
      <div className="TabloGuestAccessControl">
        <div>
          <strong>مشاهده قیمت برای کاربر مهمان</strong>
          <span>
            اگر فعال باشد کاربران بدون ورود قیمت‌ها را می‌بینند؛ اگر غیرفعال باشد به صفحه ورود زرگر هدایت می‌شوند.
          </span>
        </div>
        <SwitchButton
          onChange={handleGuestPriceAccessChange}
          defaultState={guestPriceAccess}
        />
      </div>

      <div className="SarbargNav" style={{ marginTop: "15px" }}>
        {categories.map((cat) => (
          <div
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            style={{ cursor: "pointer" }}
            className={`SarbargNavItem ${selectedCategory === cat.id ? "BgYellow ColorBlack Bold" : ""}`}
          >
            <span>{cat.name}</span>
            <span onClick={(event) => event.stopPropagation()}>
              <SwitchButton
                onChange={(v) => handleCategorySwitchChange(cat.id, v)}
                defaultState={categoryStates[cat.id] ?? true}
              />
            </span>
          </div>
        ))}
      </div>
      
      <div className="TabloNav">
        <p>وضعیت</p>
        <p>جنس</p>
        <p>وضعیت معاملات</p>
        <p>فی فروش</p>
        <p>وضعیت معاملات</p>
        <p>فی خرید</p>
      </div>
      
      {activeCategory?.items?.map((item) => {
        const current = itemStates[item.id] || {
          switch1: false,
          switch2: false,
          switch3: false,
        };
        
        const sellPricePerGram =
          current.switch3 && item.valueSell ? parseFloat(item.valueSell) / 4.3318 : 0;
        const buyPricePerGram =
          current.switch2 && item.valueBuy ? parseFloat(item.valueBuy) / 4.3318 : 0;

        return (
          <div className="LinerTablo" key={item.id}>
            
            {/* ================= 1. وضعیت جنس (Switch 1) ================= */}
            <div className="TabloItem">
              <SwitchButton
                onChange={(v) => handleSwitchChange(item.id, "switch1", v)}
                defaultState={current.switch1}
              />
            </div>
            <div className="TabloItem">
              <EditableText
                initialText={item.title}
                isEnabled={current.switch1}
                onSave={(newTitle) => console.log("Title updated:", newTitle)} 
              />
            </div>
            
            {/* ================= 2. فی فروش (Switch 3) ================= */}
            <div className="TabloItem">
              <SwitchButton
                onChange={(v) => handleSwitchChange(item.id, "switch3", v)}
                defaultState={current.switch3}
              />
            </div>
            <div>
              <div className="TabloItem">
                <div className="PriceEditorGroup">
                  <div className="PriceAdjustColumn">
                    <button
                      type="button"
                      className="PriceAdjustBtn"
                      disabled={!current.switch3}
                      onClick={() => adjustItemValue(item.id, "valueSell", item.valueSell, 50000)}
                    >
                      +50000
                    </button>
                    <button
                      type="button"
                      className="PriceAdjustBtn"
                      disabled={!current.switch3}
                      onClick={() => adjustItemValue(item.id, "valueSell", item.valueSell, -50000)}
                    >
                      -50000
                    </button>
                  </div>
                  <EditableText
                    initialText={item.valueSell || "0"}
                    isEnabled={current.switch3}
                    numeric
                    onSave={(newValue) => updateCoupledItemValue(item.id, "valueSell", newValue)}
                  />
                </div>
              </div>
              <p className="PerGramFi">
                نرخ هر گرم :
                <span>
                  {sellPricePerGram > 0 ? formatDisplayNumber(sellPricePerGram, 0) : '-'}
                </span>
              </p>
            </div>

            {/* ================= 3. فی خرید (Switch 2) ================= */}
            <div className="TabloItem">
              <SwitchButton
                onChange={(v) => handleSwitchChange(item.id, "switch2", v)}
                defaultState={current.switch2}
              />
            </div>
            <div>
              <div className="TabloItem">
                <div className="PriceEditorGroup">
                  <div className="PriceAdjustColumn">
                    <button
                      type="button"
                      className="PriceAdjustBtn"
                      disabled={!current.switch2}
                      onClick={() => adjustItemValue(item.id, "valueBuy", item.valueBuy, 50000)}
                    >
                      +50000
                    </button>
                    <button
                      type="button"
                      className="PriceAdjustBtn"
                      disabled={!current.switch2}
                      onClick={() => adjustItemValue(item.id, "valueBuy", item.valueBuy, -50000)}
                    >
                      -50000
                    </button>
                  </div>
                  <EditableText
                    initialText={item.valueBuy || "0"}
                    isEnabled={current.switch2}
                    numeric
                    onSave={(newValue) => updateCoupledItemValue(item.id, "valueBuy", newValue)}
                  />
                </div>
              </div>
              <p className="PerGramFi">
                نرخ هر گرم :
                <span>
                  {buyPricePerGram > 0 ? formatDisplayNumber(buyPricePerGram, 0) : '-'}
                </span>
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Tablo;
